import { forwardRef, Input as BaseInput, InputProps, useStyleConfig } from '@chakra-ui/react';
import { css } from '@emotion/react/macro';
import styled from '@emotion/styled/macro';
import React, { ChangeEvent, useState, useEffect } from 'react';
import colors from 'themes/default/common/colors';
import { mq } from 'styles/breakpoints';
import { rem } from 'utils/fonts';

const baseInputStyles = css`
  border-radius: 9px;
  padding: 0 19px 0 19px;
  font-size: ${rem(14)};
  ${mq.md} {
    font-size: ${rem(16)};
  }
`;

const underlinedInput = css`
  border: none;
  border-bottom: 1px solid ${colors.gray[200]};
  background: transparent;
  padding: 0;
  border-radius: 0;
  &:hover {
    border-bottom: 1px solid ${colors.gray[400]};
  }
  &:focus {
    border-bottom: 1px solid ${colors.pink[500]};
  }
`;

interface Props extends Omit<InputProps, 'variant'> {
  kind?: 'number' | 'text';
  variant?: InputProps['variant'] | 'underlined';
}

const StyledBaseInput = styled(BaseInput)<Props>`
  ${({ variant }) => (variant === 'underlined' ? underlinedInput : baseInputStyles)}
`;

// eslint-disable-next-line react/display-name
const Input = forwardRef<Props, 'input'>(({ kind = 'text', variant, onChange, defaultValue = '', ...props }, ref) => {
  const [value, setValue] = useState<string | number>('');
  let height;

  function maskInput(newValue: string): string | number {
    if (kind === 'number') {
      const numberValue = Number(newValue);
      const lastChar = newValue.charAt(newValue.length - 1);
      let result = Number.isNaN(numberValue) ? value : numberValue;

      if (newValue === '') {
        result = '';
      } else if (lastChar === '.' || lastChar === ',') {
        result = `${result}${lastChar}`;
      }

      setValue(result);
      return result;
    }
    setValue(newValue);
    return newValue;
  }

  const maxPermissibleInteger = 2147483647;

  const limitInputNumber = (newValue: number): number => Math.min(newValue, maxPermissibleInteger);

  const processInput = (newValue: string): string | number => {
    const processedValue: number | string = maskInput(newValue);
    return kind === 'number' && typeof processedValue === 'number' ? limitInputNumber(processedValue) : processedValue;
  };

  if (variant === 'underlined') {
    height = props.h || props.height || '40px';
  } else {
    height = props.h || props.height || '60px';
  }

  const styles = useStyleConfig('Input');

  useEffect(() => {
    setValue(kind === 'number' ? (defaultValue as number) : `${defaultValue}`);
  }, [defaultValue]);

  return (
    <StyledBaseInput
      ref={ref}
      focusBorderColor="pink.500"
      variant={variant}
      sx={styles}
      {...props}
      h={height}
      onChange={(e: ChangeEvent<HTMLInputElement>): void => {
        // @ts-ignore
        e.target.value = processInput(e.target.value);
        onChange?.(e);
      }}
      value={value}
    />
  );
});

export default Input;
